import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  Sparkles, TrendingUp, Eye, MousePointerClick,
  FileText, Play, RefreshCw, BarChart3
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminAutoAdsPage = () => {
  const [stats, setStats] = useState(null);
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, guidesRes] = await Promise.all([
        fetch(`${API_URL}/api/auto-ads/stats`, { credentials: 'include' }),
        fetch(`${API_URL}/api/auto-ads/guides?limit=10`, { credentials: 'include' })
      ]);

      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }

      if (guidesRes.ok) {
        const data = await guidesRes.json();
        setGuides(data.guides || []);
      }
    } catch (error) {
      toast.error('Failed to load auto-ads data');
    } finally {
      setLoading(false);
    }
  };

  const runScan = async () => {
    setScanning(true);
    try {
      const res = await fetch(`${API_URL}/api/auto-ads/scan-and-generate`, {
        method: 'POST',
        credentials: 'include'
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(`Scan complete! Created ${data.summary.ads_created.length} ads`);
        loadData(); // Reload data
      } else {
        toast.error('Failed to run scan');
      }
    } catch (error) {
      toast.error('Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const triggerFeatureAd = async () => {
    const name = prompt('Feature name:');
    const description = prompt('Feature description:');
    const location = prompt('Feature location (e.g., /app/feature):');

    if (!name || !description || !location) return;

    try {
      const res = await fetch(`${API_URL}/api/auto-ads/monitor/feature`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, description, location })
      });

      if (res.ok) {
        toast.success('Feature ad created!');
        loadData();
      } else {
        toast.error('Failed to create ad');
      }
    } catch (error) {
      toast.error('Ad creation failed');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-6">
          <div className="text-center">Loading...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-purple-600" />
              Auto Ad Generator
            </h1>
            <p className="text-sm text-muted-foreground">
              AI-powered automatic ad creation for features, products, and investments
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={triggerFeatureAd} variant="outline">
              <Play className="w-4 h-4 mr-2" />
              Test Feature Ad
            </Button>
            <Button onClick={runScan} disabled={scanning}>
              <RefreshCw className={`w-4 h-4 mr-2 ${scanning ? 'animate-spin' : ''}`} />
              {scanning ? 'Scanning...' : 'Run Scan'}
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Auto Ads</p>
                  <p className="text-2xl font-bold">{stats?.total_auto_ads || 0}</p>
                </div>
                <Sparkles className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Ads</p>
                  <p className="text-2xl font-bold">{stats?.active_auto_ads || 0}</p>
                </div>
                <TrendingUp className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Impressions</p>
                  <p className="text-2xl font-bold">{stats?.impressions?.toLocaleString() || 0}</p>
                </div>
                <Eye className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">CTR</p>
                  <p className="text-2xl font-bold">{stats?.ctr?.toFixed(2) || 0}%</p>
                </div>
                <MousePointerClick className="w-8 h-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* How It Works */}
        <Card>
          <CardHeader>
            <CardTitle>How Auto Ad Generation Works</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-3 gap-4">
              <div className="p-4 border rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <div className="bg-blue-100 dark:bg-blue-900 p-2 rounded">
                    <BarChart3 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h3 className="font-semibold">1. Detection</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  System monitors for new features, products, and investment packages
                </p>
              </div>

              <div className="p-4 border rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <div className="bg-purple-100 dark:bg-purple-900 p-2 rounded">
                    <Sparkles className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <h3 className="font-semibold">2. AI Generation</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  AI creates compelling ad copy, CTAs, and how-to guides automatically
                </p>
              </div>

              <div className="p-4 border rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <div className="bg-green-100 dark:bg-green-900 p-2 rounded">
                    <Play className="w-5 h-5 text-green-600 dark:text-green-400" />
                  </div>
                  <h3 className="font-semibold">3. Auto-Publish</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Ads are automatically published to relevant sections without approval
                </p>
              </div>
            </div>

            <div className="bg-blue-50 dark:bg-blue-950 p-4 rounded-lg">
              <h4 className="font-semibold mb-2">Automatic Triggers:</h4>
              <ul className="text-sm space-y-1 text-muted-foreground">
                <li>✓ New investment packages → Auto-creates investment ads</li>
                <li>✓ Featured products → Auto-creates product ads</li>
                <li>✓ New platform features → Auto-creates feature announcements</li>
                <li>✓ Commerce updates → Auto-creates commerce promotional ads</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Auto-Generated How-To Guides */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Auto-Generated How-To Guides
              <Badge>{guides.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {guides.length > 0 ? (
              <div className="space-y-3">
                {guides.map((guide) => (
                  <div key={guide.id} className="p-4 border rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold">{guide.title}</h3>
                          <Badge variant="secondary">{guide.category}</Badge>
                          <Badge variant="outline">Auto-generated</Badge>
                        </div>
                        <pre className="text-sm text-muted-foreground whitespace-pre-wrap">
                          {guide.content}
                        </pre>
                        <p className="text-xs text-muted-foreground mt-2">
                          Created: {new Date(guide.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-8">
                No auto-generated guides yet. Run a scan to detect new content.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Manual Triggers */}
        <Card>
          <CardHeader>
            <CardTitle>Manual Triggers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              <Button onClick={triggerFeatureAd} variant="outline" className="h-20">
                <div className="text-center">
                  <Sparkles className="w-6 h-6 mx-auto mb-1" />
                  <span className="text-sm">Create Feature Ad</span>
                </div>
              </Button>

              <Button 
                variant="outline" 
                className="h-20"
                onClick={() => {
                  const id = prompt('Enter investment package ID:');
                  if (id) {
                    fetch(`${API_URL}/api/auto-ads/monitor/investment`, {
                      method: 'POST',
                      credentials: 'include',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ package_id: id })
                    }).then(res => {
                      if (res.ok) {
                        toast.success('Investment ad created!');
                        loadData();
                      } else {
                        toast.error('Failed to create ad');
                      }
                    });
                  }
                }}
              >
                <div className="text-center">
                  <TrendingUp className="w-6 h-6 mx-auto mb-1" />
                  <span className="text-sm">Create Investment Ad</span>
                </div>
              </Button>

              <Button 
                variant="outline" 
                className="h-20"
                onClick={() => {
                  const name = prompt('Commerce feature name:');
                  const benefits = prompt('Benefits:');
                  if (name && benefits) {
                    fetch(`${API_URL}/api/auto-ads/monitor/commerce-feature`, {
                      method: 'POST',
                      credentials: 'include',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ name, benefits })
                    }).then(res => {
                      if (res.ok) {
                        toast.success('Commerce ad created!');
                        loadData();
                      } else {
                        toast.error('Failed to create ad');
                      }
                    });
                  }
                }}
              >
                <div className="text-center">
                  <FileText className="w-6 h-6 mx-auto mb-1" />
                  <span className="text-sm">Create Commerce Ad</span>
                </div>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};
